import React, { useState } from 'react';
import { useAnalysisContext } from '../../../../context/AnalysisContext';
import {
  TrendingUp,
  Users,
  Activity,
  Clock,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Settings,
  Network,
  AlertCircle,
} from 'lucide-react';
import TargetDetailsModal from '../components/TargetDetailsModal';

/**
 * Overview Tab - Summary of analysis results
 *
 * Features:
 * - Key metrics cards
 * - Top findings with rank display
 * - Execution summary
 * - Quick actions
 */

export const OverviewTab: React.FC = () => {
  const {
    analysisResult,
    selectedDisease,
    selectedAnalysisType,
    preset,
    enterRefinementMode,
    setActiveResultTab,
  } = useAnalysisContext();

  const [selectedTarget, setSelectedTarget] = useState<string | null>(null);

  if (!analysisResult) {
    return (
      <div className="p-8 text-center text-[var(--text-secondary)]">
        No results available
      </div>
    );
  }

  const lncRNAs = analysisResult.lncrna_results || [];
  const topLncRNA = lncRNAs[0];

  // Check if dual ranking is available
  const hasDualRanking = analysisResult?.targets?.[0]?.priority_rank !== undefined;

  // Debug logging
  console.log('[OverviewTab] Analysis result:', {
    hasTargets: !!analysisResult?.targets,
    targetCount: analysisResult?.targets?.length,
    firstTargetKeys: analysisResult?.targets?.[0] ? Object.keys(analysisResult.targets[0]) : [],
    hasPriorityRank: analysisResult?.targets?.[0]?.priority_rank !== undefined,
    hasDualRanking,
    n_consensus: analysisResult?.n_consensus,
    n_top_expression: analysisResult?.n_top_expression
  });

  return (
    <>
      {/* Target Details Modal */}
      {selectedTarget && (
        <TargetDetailsModal
          targetGene={selectedTarget}
          onClose={() => setSelectedTarget(null)}
        />
      )}

      <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-2">
            Analysis Complete
          </h2>
          <p className="text-[var(--text-secondary)]">
            {selectedAnalysisType.charAt(0).toUpperCase() + selectedAnalysisType.slice(1)} analysis for {selectedDisease}
          </p>
        </div>
        <button
          onClick={enterRefinementMode}
          className="origin-btn origin-btn-secondary flex items-center gap-2"
        >
          <Settings className="w-4 h-4" />
          Refine Analysis
        </button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {/* lncRNAs Found */}
        <div className="origin-card p-5">
          <div className="flex items-center justify-between mb-3">
            <Sparkles className="w-8 h-8 text-[var(--origin-blue-500)]" />
            <span className="text-2xl font-bold text-[var(--text-primary)]">
              {lncRNAs.length}
            </span>
          </div>
          <h4 className="font-medium text-[var(--text-primary)] mb-1">
            lncRNAs Identified
          </h4>
          <p className="text-xs text-[var(--text-tertiary)]">
            Disease-specific candidates
          </p>
        </div>

        {/* Disease Cells */}
        <div className="origin-card p-5">
          <div className="flex items-center justify-between mb-3">
            <Users className="w-8 h-8 text-[var(--error)]" />
            <span className="text-2xl font-bold text-[var(--text-primary)]">
              {analysisResult.n_disease_cells?.toLocaleString() || '-'}
            </span>
          </div>
          <h4 className="font-medium text-[var(--text-primary)] mb-1">
            Disease Cells
          </h4>
          <p className="text-xs text-[var(--text-tertiary)]">
            Analyzed in dataset
          </p>
        </div>

        {/* Normal Cells */}
        <div className="origin-card p-5">
          <div className="flex items-center justify-between mb-3">
            <Users className="w-8 h-8 text-[var(--success)]" />
            <span className="text-2xl font-bold text-[var(--text-primary)]">
              {analysisResult.n_normal_cells?.toLocaleString() || '-'}
            </span>
          </div>
          <h4 className="font-medium text-[var(--text-primary)] mb-1">
            Normal Cells
          </h4>
          <p className="text-xs text-[var(--text-tertiary)]">
            Control comparison
          </p>
        </div>

        {/* Execution Time */}
        <div className="origin-card p-5">
          <div className="flex items-center justify-between mb-3">
            <Clock className="w-8 h-8 text-[var(--origin-blue-500)]" />
            <span className="text-2xl font-bold text-[var(--text-primary)]">
              {analysisResult.execution_time?.toFixed(1) || '-'}s
            </span>
          </div>
          <h4 className="font-medium text-[var(--text-primary)] mb-1">
            Runtime
          </h4>
          <p className="text-xs text-[var(--text-tertiary)]">
            Using {preset} preset
          </p>
        </div>

      </div>

      {/* Dual Ranking Category Cards */}
      {hasDualRanking && (
        <div className="mb-8">
          <h3 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-4">
            Target Categories
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Consensus Targets */}
            <div className="origin-card p-5 border-l-4 border-green-500">
              <div className="flex items-center justify-between mb-3">
                <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
                <span className="text-2xl font-bold text-[var(--text-primary)]">
                  {analysisResult.n_consensus || 0}
                </span>
              </div>
              <h4 className="font-medium text-[var(--text-primary)] mb-1">
                Consensus Targets
              </h4>
              <p className="text-xs text-[var(--text-tertiary)]">
                High in both expression and network rankings
              </p>
            </div>

            {/* Expression Leaders */}
            <div className="origin-card p-5 border-l-4 border-blue-500">
              <div className="flex items-center justify-between mb-3">
                <TrendingUp className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                <span className="text-2xl font-bold text-[var(--text-primary)]">
                  {analysisResult.n_top_expression || 0}
                </span>
              </div>
              <h4 className="font-medium text-[var(--text-primary)] mb-1">
                Expression Leaders
              </h4>
              <p className="text-xs text-[var(--text-tertiary)]">
                Strong disease signal, moderate network role
              </p>
            </div>

            {/* Network Priorities */}
            <div className="origin-card p-5 border-l-4 border-purple-500">
              <div className="flex items-center justify-between mb-3">
                <Network className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                <span className="text-2xl font-bold text-[var(--text-primary)]">
                  {analysisResult.n_top_network || 0}
                </span>
              </div>
              <h4 className="font-medium text-[var(--text-primary)] mb-1">
                Network Priorities
              </h4>
              <p className="text-xs text-[var(--text-tertiary)]">
                Central regulatory hubs, moderate expression
              </p>
            </div>

            {/* Divergent Signals */}
            <div className="origin-card p-5 border-l-4 border-orange-500">
              <div className="flex items-center justify-between mb-3">
                <AlertCircle className="w-8 h-8 text-orange-600 dark:text-orange-400" />
                <span className="text-2xl font-bold text-[var(--text-primary)]">
                  {analysisResult.n_divergent || 0}
                </span>
              </div>
              <h4 className="font-medium text-[var(--text-primary)] mb-1">
                Divergent Signals
              </h4>
              <p className="text-xs text-[var(--text-tertiary)]">
                Large disagreement between ranking methods
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Top Finding */}
      {topLncRNA && (
        <div
          className="origin-card p-6 bg-gradient-to-br from-[var(--origin-blue-500)]/10 to-transparent border-[var(--origin-blue-500)] cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => setSelectedTarget(topLncRNA.lncrna)}
          title="Click to view detailed analysis"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-[var(--origin-blue-500)] flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-bold text-[var(--text-primary)] hover:text-[var(--origin-blue-500)] transition-colors">
                  Top Candidate: {topLncRNA.lncrna}
                </h3>
                <span
                  className="px-3 py-1 rounded-full text-sm font-semibold bg-[var(--origin-blue-500)]/20 text-[var(--origin-blue-500)]"
                >
                  Rank #1
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All Results Summary */}
      <div className="origin-card p-6">
        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5 text-[var(--origin-blue-500)]" />
          All Identified lncRNAs
        </h3>
        <div className="space-y-3">
          {lncRNAs.slice(0, 5).map((lncrna: any, idx: number) => (
            <div
              key={idx}
              className="flex items-center justify-between p-3 bg-[var(--bg-secondary)] rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors cursor-pointer"
              onClick={() => setSelectedTarget(lncrna.lncrna)}
              title="Click to view detailed analysis"
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[var(--origin-blue-500)]/10 text-[var(--origin-blue-500)] font-semibold text-sm">
                  {idx + 1}
                </div>
                <h4 className="font-semibold text-[var(--text-primary)] hover:text-[var(--origin-blue-500)] transition-colors">
                  {lncrna.lncrna}
                </h4>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-semibold text-sm text-[var(--origin-blue-500)]">
                  Rank #{lncrna.rank || (idx + 1)}
                </span>
              </div>
            </div>
          ))}
        </div>

        {lncRNAs.length > 5 && (
          <div className="mt-4 text-center">
            <button
              onClick={() => setActiveResultTab('table')}
              className="text-sm text-[var(--origin-blue-500)] hover:text-[var(--origin-blue-600)] font-medium flex items-center gap-1 mx-auto"
            >
              View all {lncRNAs.length} results
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Next Steps */}
      <div className="origin-card p-6">
        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-[var(--success)]" />
          Next Steps
        </h3>
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-[var(--origin-blue-500)]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-semibold text-[var(--origin-blue-500)]">1</span>
            </div>
            <div>
              <p className="font-medium text-[var(--text-primary)]">Review identified targets</p>
              <p className="text-sm text-[var(--text-secondary)]">
                Navigate to the Targets tab to explore all identified lncRNAs with priority scores and metrics
              </p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-[var(--origin-blue-500)]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-semibold text-[var(--origin-blue-500)]">2</span>
            </div>
            <div>
              <p className="font-medium text-[var(--text-primary)]">Validate top candidates</p>
              <p className="text-sm text-[var(--text-secondary)]">
                Build GRNs and simulate knockdown for high-priority targets to validate therapeutic potential
              </p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-[var(--origin-blue-500)]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-semibold text-[var(--origin-blue-500)]">3</span>
            </div>
            <div>
              <p className="font-medium text-[var(--text-primary)]">Export and share results</p>
              <p className="text-sm text-[var(--text-secondary)]">
                Download target list in CSV or JSON format from the Export tab for further analysis
              </p>
            </div>
          </li>
        </ul>
      </div>
      </div>
    </>
  );
};

export default OverviewTab;
